// src/pages/Testimonials.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, orderBy, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import LoadingSpinner from '../components/LoadingSpinner';

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [selectedRating, setSelectedRating] = useState(5);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [filterRating, setFilterRating] = useState('all');
  const [currentTime, setCurrentTime] = useState('');

  const [formData, setFormData] = useState({
    studentName: '',
    course: '',
    review: '',
    rating: 5,
    image: ''
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }));
    };
    updateClock();
    const clockInterval = setInterval(updateClock, 1000);

    const q = query(collection(db, 'testimonials'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const testimonialData = [];
      snapshot.forEach((doc) => {
        testimonialData.push({ id: doc.id, ...doc.data() });
      });
      setTestimonials(testimonialData);
      setLoading(false);
    });

    return () => {
      clearInterval(clockInterval);
      unsubscribe();
    };
  }, []);

  const resetForm = () => {
    setFormData({
      studentName: '',
      course: '',
      review: '',
      rating: 5,
      image: ''
    });
    setImageFile(null);
    setImagePreview('');
    setEditingTestimonial(null);
    setSelectedRating(5);
    setUploadProgress(0);
    setIsUploading(false);
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingTestimonial(item);
    setFormData({
      studentName: item.studentName || '',
      course: item.course || '',
      review: item.review || '',
      rating: item.rating || 5,
      image: item.image || ''
    });
    setSelectedRating(item.rating || 5);
    if (item.image) setImagePreview(item.image);
    setModalOpen(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setIsUploading(true);

    try {
      let imageUrl = formData.image;

      if (imageFile) {
        const storageRef = ref(storage, `testimonials/${Date.now()}_${imageFile.name}`);
        const uploadTask = uploadBytesResumable(storageRef, imageFile);
        await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
            },
            (error) => reject(error),
            async () => {
              imageUrl = await getDownloadURL(uploadTask.snapshot.ref);
              resolve();
            }
          );
        });
      }

      const testimonialData = {
        studentName: formData.studentName,
        course: formData.course,
        review: formData.review,
        rating: selectedRating,
        image: imageUrl,
        updatedAt: new Date()
      };

      if (editingTestimonial) {
        if (formData.image && imageFile && formData.image !== imageUrl) {
          try { await deleteObject(ref(storage, formData.image)); } catch (e) {}
        }
        await updateDoc(doc(db, 'testimonials', editingTestimonial.id), testimonialData);
        alert('Testimonial updated successfully!');
      } else {
        testimonialData.createdAt = new Date();
        await addDoc(collection(db, 'testimonials'), testimonialData);
        alert('Testimonial added successfully!');
      }

      setModalOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving testimonial:', error);
      alert('Error saving testimonial: ' + error.message);
    } finally {
      setLoading(false);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id, imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    
    setLoading(true);
    try {
      if (imageUrl) {
        try { await deleteObject(ref(storage, imageUrl)); } catch (e) {}
      }
      await deleteDoc(doc(db, 'testimonials', id));
      alert('Testimonial deleted successfully!');
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      alert('Error deleting testimonial: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    resetForm();
  };

  const getFilteredTestimonials = () => {
    if (filterRating === 'all') return testimonials;
    const minRating = parseInt(filterRating);
    return testimonials.filter(t => (t.rating || 0) >= minRating);
  };

  const getStats = () => {
    const total = testimonials.length;
    const avgRating = testimonials.reduce((sum, t) => sum + (t.rating || 0), 0) / total || 0;
    const fiveStar = testimonials.filter(t => t.rating === 5).length;
    return { total, avgRating, fiveStar };
  };

  const stats = getStats();

  const formatDate = (timestamp) => {
    if (!timestamp) return 'No date';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return 'Invalid date';
    }
  };

  const generateStarRating = (rating) => {
    let stars = '';
    for (let i = 1; i <= 5; i++) {
      if (i <= rating) {
        stars += '<i class="fas fa-star" style="color: #fbbf24;"></i>';
      } else {
        stars += '<i class="far fa-star" style="color: rgba(251,191,36,0.4);"></i>';
      }
    }
    return stars;
  };

  const styles = `
    .testimonials-page {
      animation: structuralSlideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
    }

    .management-toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 32px;
      background: rgba(255, 255, 255, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.6);
      padding: 16px 24px;
      border-radius: 16px;
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      flex-wrap: wrap;
      gap: 16px;
    }

    .filter-select {
      padding: 10px 20px;
      border: 2px solid rgba(14, 165, 233, 0.2);
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.6);
      cursor: pointer;
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
      outline: none;
      transition: all 0.2s;
    }

    .filter-select:focus {
      border-color: #0ea5e9;
      background: #ffffff;
      box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.1);
    }

    .btn-action-trigger {
      padding: 12px 24px;
      border: none;
      border-radius: 12px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.85rem;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      box-shadow: 0 4px 15px rgba(14, 165, 233, 0.2);
    }

    .btn-action-trigger:hover {
      transform: translateY(-3px);
    }

    .btn-blue {
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
    }

    .btn-blue:hover {
      box-shadow: 0 10px 25px rgba(14, 165, 233, 0.4);
    }

    .testimonials-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
      gap: 28px;
    }

    .testimonial-card {
      background: rgba(255, 255, 255, 0.45);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border-radius: 16px;
      overflow: hidden;
      border: 1px solid rgba(255, 255, 255, 0.6);
      box-shadow: 0 8px 32px 0 rgba(14, 165, 233, 0.06);
      transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
      display: flex;
      flex-direction: column;
    }

    .testimonial-card:hover {
      transform: translateY(-6px);
      background: rgba(255, 255, 255, 0.65);
      box-shadow: 0 16px 36px 0 rgba(14, 165, 233, 0.14);
      border-color: rgba(14, 165, 233, 0.3);
    }

    .testimonial-card-header {
      background: linear-gradient(135deg, #0f172a, #1e293b);
      padding: 30px 24px;
      text-align: center;
      position: relative;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    }

    .testimonial-avatar {
      width: 76px;
      height: 76px;
      border-radius: 50%;
      object-fit: cover;
      border: 3px solid rgba(14, 165, 233, 0.4);
      margin-bottom: 12px;
      background: rgba(255, 255, 255, 0.1);
    }

    .testimonial-name {
      font-family: 'Space Grotesk', sans-serif;
      color: white;
      font-size: 1.2rem;
      font-weight: 700;
      margin-bottom: 4px;
    }

    .testimonial-course {
      color: #94a3b8;
      font-size: 0.85rem;
      font-weight: 600;
    }

    .rating-stars {
      margin-top: 10px;
      font-size: 0.95rem;
    }

    .quote-icon-badge {
      position: absolute;
      bottom: -20px;
      right: 24px;
      width: 40px;
      height: 40px;
      background: rgba(255, 255, 255, 0.9);
      backdrop-filter: blur(8px);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #0ea5e9;
      font-size: 16px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      border: 1px solid rgba(255,255,255,0.6);
      z-index: 2;
    }

    .testimonial-card-content {
      padding: 28px 24px 24px 24px;
      display: flex;
      flex-direction: column;
      flex: 1;
    }

    .testimonial-review-text {
      color: #334155;
      font-size: 0.92rem;
      line-height: 1.6;
      margin-bottom: 20px;
      font-style: italic;
      font-weight: 600;
    }

    .testimonial-card-meta {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 0.78rem;
      color: #64748b;
      font-weight: 600;
      padding-top: 12px;
      border-top: 1px solid rgba(14, 165, 233, 0.1);
      margin-top: auto;
    }

    .testimonial-card-actions {
      display: flex;
      gap: 10px;
      padding: 16px 24px;
      border-top: 1px solid rgba(14, 165, 233, 0.1);
    }

    .btn-row-action {
      flex: 1;
      padding: 10px 14px;
      border-radius: 8px;
      font-weight: 700;
      font-size: 0.8rem;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      transition: all 0.2s;
      border: none;
    }

    .btn-row-edit {
      background: rgba(14, 165, 233, 0.12);
      border: 1px solid rgba(14, 165, 233, 0.3);
      color: #0ea5e9;
    }

    .btn-row-edit:hover {
      background: #0ea5e9;
      color: white;
    }

    .btn-row-delete {
      background: rgba(244, 63, 94, 0.1);
      border: 1px solid rgba(244, 63, 94, 0.25);
      color: #f43f5e;
    }

    .btn-row-delete:hover {
      background: #f43f5e;
      color: white;
    }

    .empty-state {
      text-align: center;
      padding: 60px 20px;
      background: rgba(255, 255, 255, 0.45);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border-radius: 16px;
      border: 1px solid rgba(255, 255, 255, 0.6);
      grid-column: 1/-1;
    }

    .empty-state i {
      font-size: 54px;
      color: #64748b;
      margin-bottom: 16px;
      display: block;
    }

    .empty-state h3 {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.2rem;
      margin-bottom: 6px;
      color: #0f172a;
    }

    .empty-state p {
      color: #64748b;
      font-size: 0.88rem;
      margin-bottom: 20px;
    }

    /* Modal Styles - Positioned at Top */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      min-height: 100vh;
      background: rgba(15, 23, 42, 0.7);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      z-index: 99999;
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding: 40px 20px 80px 20px;
      overflow-y: auto;
      animation: fadeIn 0.3s ease;
    }

    .modal-viewport-box {
      background: rgba(255, 255, 255, 0.98);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border: 1px solid rgba(255, 255, 255, 0.6);
      border-radius: 24px;
      padding: 40px;
      width: 100%;
      max-width: 600px;
      max-height: none;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      animation: slideDown 0.3s ease;
      margin-bottom: 40px;
    }

    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .modal-viewport-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      padding-bottom: 16px;
      border-bottom: 2px solid rgba(14, 165, 233, 0.1);
    }

    .modal-viewport-header h2 {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.4rem;
      color: #0f172a;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .modal-viewport-header h2 i {
      color: #0ea5e9;
    }

    .modal-close-btn {
      width: 40px;
      height: 40px;
      background: rgba(244, 63, 94, 0.1);
      border: 1px solid rgba(244, 63, 94, 0.25);
      border-radius: 12px;
      color: #f43f5e;
      cursor: pointer;
      font-size: 1.2rem;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .modal-close-btn:hover {
      background: #f43f5e;
      color: white;
      transform: rotate(90deg);
    }

    .form-group {
      margin-bottom: 20px;
    }

    .form-group label {
      display: block;
      margin-bottom: 8px;
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
    }

    .form-group label .required {
      color: #f43f5e;
      margin-left: 4px;
    }

    .form-group input,
    .form-group textarea,
    .form-group select {
      width: 100%;
      padding: 12px 16px;
      border: 2px solid rgba(14, 165, 233, 0.15);
      border-radius: 12px;
      font-size: 14px;
      background: rgba(255, 255, 255, 0.6);
      color: #0f172a;
      outline: none;
      transition: all 0.3s ease;
      font-family: inherit;
    }

    .form-group input:focus,
    .form-group textarea:focus,
    .form-group select:focus {
      border-color: #0ea5e9;
      background: #ffffff;
      box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.1);
    }

    .form-group textarea {
      resize: vertical;
      min-height: 100px;
    }

    .rating-selector-matrix {
      display: flex;
      gap: 12px;
      font-size: 26px;
      color: #cbd5e1;
      cursor: pointer;
    }

    .rating-selector-matrix i {
      transition: color 0.15s ease, transform 0.15s ease;
    }

    .rating-selector-matrix i:hover {
      transform: scale(1.15);
    }

    .rating-selector-matrix i.active {
      color: #fbbf24;
    }

    .file-upload-wrapper {
      position: relative;
      border: 2px dashed rgba(14, 165, 233, 0.3);
      border-radius: 12px;
      padding: 16px;
      text-align: center;
      cursor: pointer;
      transition: all 0.3s ease;
      background: rgba(255, 255, 255, 0.3);
    }

    .file-upload-wrapper:hover {
      border-color: #0ea5e9;
      background: rgba(255, 255, 255, 0.5);
    }

    .file-upload-wrapper input[type="file"] {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      cursor: pointer;
    }

    .file-upload-wrapper .upload-icon {
      font-size: 1.5rem;
      color: #0ea5e9;
      margin-bottom: 4px;
      display: block;
    }

    .file-upload-wrapper .upload-text {
      color: #64748b;
      font-size: 0.8rem;
      font-weight: 600;
    }

    .file-upload-wrapper .upload-text span {
      color: #0ea5e9;
      font-weight: 700;
    }

    .image-preview-wrapper {
      margin-top: 12px;
      position: relative;
      display: inline-block;
      border-radius: 50%;
      overflow: hidden;
      border: 2px solid rgba(14, 165, 233, 0.2);
      width: 120px;
      height: 120px;
    }

    .image-preview-wrapper img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    .image-preview-wrapper .remove-image {
      position: absolute;
      top: 4px;
      right: 4px;
      background: rgba(244, 63, 94, 0.9);
      color: white;
      border: none;
      border-radius: 50%;
      width: 24px;
      height: 24px;
      cursor: pointer;
      font-size: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
    }

    .image-preview-wrapper .remove-image:hover {
      transform: scale(1.1);
      background: #f43f5e;
    }

    .upload-progress {
      margin-top: 12px;
      background: rgba(14, 165, 233, 0.1);
      border-radius: 8px;
      height: 6px;
      overflow: hidden;
    }

    .upload-progress .progress-bar {
      height: 100%;
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      transition: width 0.3s ease;
      border-radius: 8px;
    }

    .modal-footer {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      margin-top: 24px;
      padding-top: 16px;
      border-top: 2px solid rgba(14, 165, 233, 0.1);
    }

    .btn-cancel {
      padding: 10px 24px;
      background: rgba(255, 255, 255, 0.4);
      border: 2px solid rgba(14, 165, 233, 0.2);
      border-radius: 12px;
      color: #334155;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.9rem;
      transition: all 0.3s ease;
    }

    .btn-cancel:hover {
      background: #ffffff;
      border-color: #334155;
      transform: translateY(-2px);
    }

    .btn-submit {
      padding: 10px 28px;
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
      border: none;
      border-radius: 12px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.9rem;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      gap: 10px;
    }

    .btn-submit:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 10px 25px rgba(14, 165, 233, 0.4);
    }

    .btn-submit:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .btn-submit .spinner {
      width: 18px;
      height: 18px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top-color: white;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    @media (max-width: 768px) {
      .testimonials-grid {
        grid-template-columns: 1fr;
      }
      .management-toolbar {
        flex-direction: column;
        align-items: stretch;
      }
      .modal-viewport-box {
        padding: 24px;
        margin: 10px;
      }
      .modal-viewport-header h2 {
        font-size: 1.2rem;
      }
      .modal-footer {
        flex-direction: column;
      }
      .modal-footer button {
        width: 100%;
        justify-content: center;
      }
    }
  `;

  const filteredTestimonials = getFilteredTestimonials();

  if (loading && testimonials.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <style>{styles}</style>
      <div className="testimonials-page">
        <div className="welcome-hero">
          <h1>⭐ Testimonials Management</h1>
          <p>Configure public student feedback matrices, scale dynamic rating channels, and inspect system reviews.</p>
          <div className="hero-meta-date">
            <i className="far fa-clock"></i> {currentTime}
          </div>
        </div>

        <div className="stats-matrix" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="matrix-card card-blue">
            <div>
              <div className="matrix-value">{stats.total}</div>
              <div className="matrix-label">Total Testimonials</div>
            </div>
            <div className="matrix-icon-box"><i className="fas fa-comment-dots"></i></div>
          </div>
          <div className="matrix-card card-amber">
            <div>
              <div className="matrix-value">{stats.avgRating.toFixed(1)}</div>
              <div className="matrix-label">Average Rating</div>
            </div>
            <div className="matrix-icon-box"><i className="fas fa-star"></i></div>
          </div>
          <div className="matrix-card card-emerald">
            <div>
              <div className="matrix-value">{stats.fiveStar}</div>
              <div className="matrix-label">5-Star Reviews</div>
            </div>
            <div className="matrix-icon-box"><i className="fas fa-crown"></i></div>
          </div>
        </div>

        <div className="management-toolbar">
          <div>
            <select 
              className="filter-select" 
              value={filterRating} 
              onChange={(e) => setFilterRating(e.target.value)}
            >
              <option value="all">All Ratings</option>
              <option value="5">5 Stars Only</option>
              <option value="4">4 Stars & Above</option>
              <option value="3">3 Stars & Above</option>
            </select>
          </div>
          <button className="btn-action-trigger btn-blue" onClick={openCreateModal}>
            <i className="fas fa-plus-circle"></i> Add Testimonial
          </button>
        </div>

        <div className="testimonials-grid">
          {filteredTestimonials.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-star-slash"></i>
              <h3>No Testimonials Found</h3>
              <p>The database register holds zero feedback records under this sorting index filter.</p>
              <button className="btn-action-trigger btn-blue" onClick={openCreateModal}>
                <i className="fas fa-plus-circle"></i> Add First Testimonial
              </button>
            </div>
          ) : (
            filteredTestimonials.map((item) => (
              <div className="testimonial-card" key={item.id}>
                <div className="testimonial-card-header">
                  {item.image ? (
                    <img src={item.image} alt={item.studentName} className="testimonial-avatar" />
                  ) : (
                    <div className="testimonial-avatar" style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(255,255,255,0.15)'
                    }}>
                      <i className="fas fa-user-graduate" style={{ fontSize: '32px', color: 'rgba(255,255,255,0.8)' }}></i>
                    </div>
                  )}
                  <h3 className="testimonial-name">{item.studentName || 'Anonymous Submitter'}</h3>
                  <p className="testimonial-course">{item.course || 'Degree Program'}</p>
                  <div className="rating-stars" dangerouslySetInnerHTML={{ __html: generateStarRating(item.rating || 5) }} />
                  <div className="quote-icon-badge">
                    <i className="fas fa-quote-right"></i>
                  </div>
                </div>
                <div className="testimonial-card-content">
                  <p className="testimonial-review-text">"{item.review || 'No written feedback description text provided.'}"</p>
                  <div className="testimonial-card-meta">
                    <span><i className="far fa-calendar-alt"></i> Created: {formatDate(item.createdAt)}</span>
                  </div>
                </div>
                <div className="testimonial-card-actions">
                  <button className="btn-row-action btn-row-edit" onClick={() => openEditModal(item)}>
                    <i className="fas fa-pen-to-square"></i> Edit
                  </button>
                  <button className="btn-row-action btn-row-delete" onClick={() => handleDelete(item.id, item.image)}>
                    <i className="fas fa-trash-can"></i> Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Testimonial Modal - Positioned at Top */}
        {modalOpen && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-viewport-box" onClick={(e) => e.stopPropagation()}>
              <div className="modal-viewport-header">
                <h2>
                  <i className={`fas ${editingTestimonial ? 'fa-edit' : 'fa-plus-circle'}`}></i>
                  {editingTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}
                </h2>
                <button className="modal-close-btn" onClick={closeModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Student Name <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.studentName}
                    onChange={(e) => setFormData({ ...formData, studentName: e.target.value })}
                    placeholder="e.g., John Doe"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Course / Program <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.course}
                    onChange={(e) => setFormData({ ...formData, course: e.target.value })}
                    placeholder="e.g., B.Tech Computer Science"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Review / Testimonial <span className="required">*</span></label>
                  <textarea
                    value={formData.review}
                    onChange={(e) => setFormData({ ...formData, review: e.target.value })}
                    rows="4"
                    placeholder="Type student feedback reviews statement logs..."
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Rating <span className="required">*</span></label>
                  <div className="rating-selector-matrix">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i
                        key={star}
                        className={`fas fa-star ${selectedRating >= star ? 'active' : ''}`}
                        onClick={() => setSelectedRating(star)}
                      />
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Student Photo</label>
                  <div className="file-upload-wrapper">
                    <i className="fas fa-cloud-upload-alt upload-icon"></i>
                    <div className="upload-text">
                      Drag & drop or <span>browse</span> to upload photo
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </div>
                  {imagePreview && (
                    <div className="image-preview-wrapper">
                      <img src={imagePreview} alt="Preview" />
                      <button
                        type="button"
                        className="remove-image"
                        onClick={() => {
                          setImagePreview('');
                          setImageFile(null);
                          setFormData({ ...formData, image: '' });
                        }}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  )}
                </div>

                {isUploading && uploadProgress > 0 && (
                  <div className="upload-progress">
                    <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                  </div>
                )}

                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={closeModal}>
                    <i className="fas fa-times"></i> Cancel
                  </button>
                  <button type="submit" className="btn-submit" disabled={isUploading}>
                    {isUploading ? (
                      <>
                        <span className="spinner"></span>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save"></i>
                        {editingTestimonial ? 'Update Testimonial' : 'Add Testimonial'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Testimonials;